/**
 * OrganizationProvider
 * Manages current organization context for multi-tenant data isolation
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMyOrganizations } from '../hooks/useOrganizations';
import { useClerkUserContext } from './ClerkUserProvider';
import { setCurrentOrganization } from '../lib/api';
import CreateOrganizationPage from '../pages/CreateOrganizationPage';
import { Box, CircularProgress, Typography } from '@mui/material';

const OrganizationContext = createContext(null);

// Local storage key for selected organization
const SELECTED_ORG_KEY = 'palmera_selected_organization';

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
}

export function OrganizationProvider({ children }) {
  const { synced: clerkSynced } = useClerkUserContext();
  const queryClient = useQueryClient();
  const previousOrgId = useRef(null);
  const [selectedOrgId, setSelectedOrgId] = useState(() => {
    // Try to get from localStorage on initial load
    return localStorage.getItem(SELECTED_ORG_KEY) || null;
  });
  
  // Fetch organizations only when Clerk user is synced
  const { 
    data: orgsData, 
    isLoading, 
    error,
    refetch 
  } = useMyOrganizations({
    enabled: clerkSynced,
  });

  const organizations = orgsData?.organizations || [];
  const defaultOrgId = orgsData?.defaultOrganization;

  // Find current organization
  const currentOrganization = organizations.find(org => org.id === selectedOrgId) || 
                              organizations.find(org => org.id === defaultOrgId) ||
                              organizations[0] || null;

  // Update selected org when data loads
  useEffect(() => {
    if (organizations.length > 0 && !selectedOrgId) {
      const orgToSelect = defaultOrgId || organizations[0]?.id;
      if (orgToSelect) {
        setSelectedOrgId(orgToSelect);
        localStorage.setItem(SELECTED_ORG_KEY, orgToSelect);
      }
    }
  }, [organizations, defaultOrgId, selectedOrgId]);

  // Switch organization
  const switchOrganization = useCallback((orgId) => {
    const org = organizations.find(o => o.id === orgId);
    if (org) {
      setSelectedOrgId(orgId);
      localStorage.setItem(SELECTED_ORG_KEY, orgId);
      setCurrentOrganization(orgId);
      console.log('[OrganizationProvider] Switched to:', org.name);
      
      // Clear React Query cache to force refetch with new organization
      queryClient.clear();
      
      // Dispatch event so other contexts can refresh
      window.dispatchEvent(new CustomEvent('organizationChanged', { detail: { orgId } }));
    }
  }, [organizations, queryClient]);

  // Check permissions
  const hasPermission = useCallback((permission) => {
    if (!currentOrganization) return false;
    
    // Owner has all permissions
    if (currentOrganization.role === 'owner') return true;
    
    // Check custom permissions if defined
    if (currentOrganization.permissions) {
      try {
        const perms = JSON.parse(currentOrganization.permissions);
        return perms.includes(permission) || perms.includes('*');
      } catch {
        return false;
      }
    }
    
    // Default role-based permissions
    const rolePermissions = {
      admin: ['employees', 'shifts', 'salaries', 'expenses', 'revenue', 'settings', 'reports'],
      manager: ['employees.view', 'shifts', 'salaries.view', 'expenses.view', 'revenue', 'reports'],
      employee: ['shifts.view', 'salaries.view.own'],
    };
    
    const allowedPerms = rolePermissions[currentOrganization.role] || [];
    return allowedPerms.some(p => permission.startsWith(p));
  }, [currentOrganization]);

  // Check role level
  const hasRole = useCallback((requiredRole) => {
    if (!currentOrganization) return false;
    
    const roleLevels = { owner: 100, admin: 80, manager: 50, employee: 10 };
    const userLevel = roleLevels[currentOrganization.role] || 0;
    const requiredLevel = roleLevels[requiredRole] || 0;
    
    return userLevel >= requiredLevel;
  }, [currentOrganization]);

  const contextValue = {
    // Current organization
    organization: currentOrganization,
    organizationId: currentOrganization?.id || null,
    organizationName: currentOrganization?.name || 'Loading...',
    
    // All organizations
    organizations,
    
    // User's role in current org
    role: currentOrganization?.role || null,
    
    // Loading state
    isLoading,
    error,
    
    // Actions
    switchOrganization,
    refetchOrganizations: refetch,
    
    // Permission checks
    hasPermission,
    hasRole,
    
    // Is multi-org user
    hasMultipleOrgs: organizations.length > 1,
  };

  // Sync organization ID to API client - do this SYNCHRONOUSLY
  // This ensures the org ID is set before any child components render
  const [orgSynced, setOrgSynced] = useState(false);
  
  useEffect(() => {
    if (currentOrganization?.id) {
      // Check if organization changed
      if (previousOrgId.current && previousOrgId.current !== currentOrganization.id) {
        console.log('[OrganizationProvider] Org changed, invalidating cache');
        // Invalidate all queries to refetch with new organization
        queryClient.invalidateQueries();
      }
      
      previousOrgId.current = currentOrganization.id;
      setCurrentOrganization(currentOrganization.id);
      setOrgSynced(true);
      console.log('[OrganizationProvider] Current org:', {
        id: currentOrganization.id,
        name: currentOrganization.name,
        role: currentOrganization.role
      });
    } else {
      setOrgSynced(false);
    }
  }, [currentOrganization, queryClient]);

  // Show loading state while fetching organizations OR waiting for org to sync
  if (isLoading || !clerkSynced || (organizations.length > 0 && !orgSynced)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          gap: 2,
        }}
      >
        <CircularProgress color="primary" />
        <Typography color="text.secondary">
          Φόρτωση...
        </Typography>
      </Box>
    );
  }

  // Show create organization page if user has no organizations
  if (!isLoading && clerkSynced && organizations.length === 0) {
    return (
      <OrganizationContext.Provider value={contextValue}>
        <CreateOrganizationPage />
      </OrganizationContext.Provider>
    );
  }

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
}

export default OrganizationProvider;

